import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  AlertTitle,
  LinearProgress
} from '@mui/material';
import { LocationOn, CheckCircle, GpsFixed } from '@mui/icons-material';
import { submitMobileLocation } from './user/GeolocationManager/services/geolocationService';

// Интерфейс для результата GPS
interface GPSResult {
  latitude: number;
  longitude: number;
  accuracy: number;
}

/**
 * Страница для мобильного устройства
 * Открывается при сканировании QR-кода
 * Использует ULTRA HIGH ACCURACY режим с watchPosition
 */
const MobileGeoPage: React.FC = () => {
  const [token, setToken] = useState<string>('');
  const [status, setStatus] = useState<'init' | 'requesting' | 'success' | 'error'>('init');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [accuracy, setAccuracy] = useState<number>(0);
  const [measurementCount, setMeasurementCount] = useState<number>(0);
  const [progress, setProgress] = useState<number>(0);
  const [userGuid, setUserGuid] = useState<string | null>(null);

  // Получаем токен и GUID из URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    const guidParam = urlParams.get('guid');

    if (tokenParam) {
      setToken(tokenParam);
      setUserGuid(guidParam);
    } else {
      setStatus('error');
      setErrorMessage('Неверная ссылка. QR-код поврежден или истек.');
    }
  }, []);

  /**
   * ⭐ УЛЬТРА ТОЧНЫЙ режим GPS
   * Использует watchPosition для непрерывного мониторинга
   * Ждет пока точность не станет < 10м или макс. 45 секунд
   */
  const handleRequestLocation = async () => {
    if (!token) {
      setStatus('error');
      setErrorMessage('Токен не найден');
      return;
    }

    setStatus('requesting');
    setErrorMessage('');
    setMeasurementCount(0);
    setProgress(0);

    try {
      // Проверяем поддержку геолокации
      if (!navigator.geolocation) {
        throw new Error('Ваше устройство не поддерживает геолокацию');
      }

      console.log('🛰️ Запуск ULTRA HIGH ACCURACY GPS трекинга...');

      // Запрашиваем координаты с НЕПРЕРЫВНЫМ отслеживанием
      const gpsResult = await new Promise<GPSResult>((resolve, reject) => {
        let bestPosition: GPSResult | null = null;
        let bestAccuracy = Infinity;
        let watchId: number;
        let timeoutId: NodeJS.Timeout;
        let measurements = 0;
        const TARGET_ACCURACY = 10; // Целевая точность 10 метров
        const MAX_TIME = 45000; // Максимум 45 секунд
        const startTime = Date.now();

        const options: PositionOptions = {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        };

        const finish = (result: GPSResult) => {
          if (watchId !== undefined) {
            navigator.geolocation.clearWatch(watchId);
          }
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          resolve(result);
        };

        watchId = navigator.geolocation.watchPosition(
          (pos) => {
            measurements++;
            const currentAccuracy = pos.coords.accuracy;
            const elapsed = Date.now() - startTime;
            const progressPercent = Math.min((elapsed / MAX_TIME) * 100, 100);

            setMeasurementCount(measurements);
            setProgress(progressPercent);
            setAccuracy(currentAccuracy);

            console.log(`📡 GPS #${measurements}: ${currentAccuracy.toFixed(2)}м (${(elapsed / 1000).toFixed(1)}с)`);

            if (currentAccuracy < bestAccuracy) {
              bestAccuracy = currentAccuracy;
              bestPosition = {
                latitude: pos.coords.latitude,
                longitude: pos.coords.longitude,
                accuracy: currentAccuracy
              };

              if (currentAccuracy <= TARGET_ACCURACY) {
                finish(bestPosition);
              }
            }

            if (measurements >= 5 && currentAccuracy < 30 && elapsed > 10000) {
              finish(bestPosition!);
            }
          },
          (error) => {
            if (bestPosition) {
              console.warn(`⚠️ Ошибка GPS, но есть результат: ${bestAccuracy.toFixed(2)}м`);
              finish(bestPosition);
            } else {
              if (watchId !== undefined) {
                navigator.geolocation.clearWatch(watchId);
              }
              if (timeoutId) {
                clearTimeout(timeoutId);
              }
              reject(error);
            }
          },
          options
        );

        // Таймаут - возвращаем лучший результат
        timeoutId = setTimeout(() => {
          if (bestPosition) {
            console.log(`⏰ Таймаут ${MAX_TIME / 1000}с. Лучший результат: ${bestAccuracy.toFixed(2)}м из ${measurements} измерений`);
            finish(bestPosition);
          } else {
            if (watchId !== undefined) {
              navigator.geolocation.clearWatch(watchId);
            }
            reject(new Error('Таймаут без результатов'));
          }
        }, MAX_TIME);
      });

      const latitude = gpsResult.latitude;
      const longitude = gpsResult.longitude;
      const posAccuracy = gpsResult.accuracy;

      setAccuracy(posAccuracy);

      console.log(`📍 Финальные координаты: ${latitude.toFixed(7)}, ${longitude.toFixed(7)} (точность: ${posAccuracy.toFixed(2)}м)`);

      // Пробуем получить адрес
      let address: string | undefined;
      try {
        const addressModule = await import('./user/GeolocationManager/services/geolocationService');
        const addressResponse = await addressModule.getAddressByCoords(latitude, longitude);
        if (addressResponse.success && addressResponse.address) {
          address = addressResponse.address;
          console.log(`📍 Адрес: ${address}`);
        }
      } catch (err) {
        console.warn('Не удалось получить адрес');
      }

      // Отправляем на сервер с GUID
      const response = await submitMobileLocation(
        token,
        latitude,
        longitude,
        posAccuracy,
        address,
        userGuid || undefined
      );

      if (response.success) {
        setStatus('success');
        console.log(' Координаты успешно отправлены');

        // Закрываем страницу через 3 секунды
        setTimeout(() => {
          window.close();
        }, 3000);
      } else {
        throw new Error(response.message || 'Не удалось отправить координаты');
      }

    } catch (error: any) {
      console.error('Ошибка:', error);
      setStatus('error');

      if (error.code === 1) {
        setErrorMessage('Вы отклонили доступ к местоположению. Разрешите доступ в настройках браузера.');
      } else if (error.code === 2) {
        setErrorMessage('Не удалось определить местоположение. Проверьте, что GPS включен или используйте другое устройство.');
      } else if (error.code === 3) {
        setErrorMessage('Превышено время ожидания. Попробуйте еще раз.');
      } else {
        setErrorMessage(error.message || 'Произошла ошибка');
      }
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        {/* Заголовок */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <LocationOn sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography variant="h5" fontWeight="bold" textAlign="center">
            Определение местоположения
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1 }}>
            Zerde Pharmacy
          </Typography>
        </Box>

        {/* Контент */}
        {status === 'init' && token && (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              <AlertTitle>🛰️ Требуется GPS</AlertTitle>
              <Typography variant="body2">
                Для <strong>максимальной точности</strong> (5-15м):
                <br />
                <br />
                <strong>Обязательно:</strong>
                <br /> Включите GPS (не только WiFi/сеть)
                <br /> Выйдите на улицу (открытое пространство)
                <br /> Держите телефон неподвижно
                <br /> Дождитесь стабилизации (~30-45 сек)
                <br />
                <br />
                <strong>Система будет:</strong>
                <br />📡 Непрерывно отслеживать GPS
                <br />🎯 Ждать точности &lt; 10м
                <br />⏱️ Макс. время: 45 секунд
              </Typography>
            </Alert>

            <Button
              variant="contained"
              size="large"
              fullWidth
              startIcon={<GpsFixed />}
              onClick={handleRequestLocation}
              sx={{ py: 1.5 }}
            >
              Начать GPS-трекинг
            </Button>
          </Box>
        )}

        {status === 'requesting' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 3 }}>
            <CircularProgress size={64} />
            <Typography variant="h6" textAlign="center">
              GPS трекинг активен...
            </Typography>

            {/* Прогресс бар */}
            <Box sx={{ width: '100%', mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Измерений: {measurementCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {accuracy > 0 ? `${accuracy.toFixed(1)}м` : '...'}
                </Typography>
              </Box>
              <LinearProgress variant="determinate" value={progress} />
            </Box>

            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 2 }}>
              🛰️ Ожидание стабилизации GPS...
              <br />
              Держите телефон неподвижно на открытом пространстве
              <br />
              <br />
              {accuracy > 0 && accuracy <= 10 && '🎯 Отличная точность! Скоро завершим...'}
              {accuracy > 10 && accuracy <= 30 && ' Хорошая точность, продолжаем улучшать...'}
              {accuracy > 30 && '📡 Захват спутников, подождите...'}
            </Typography>
          </Box>
        )}

        {status === 'success' && (
          <Box>
            <Alert severity="success">
              <AlertTitle> Успешно!</AlertTitle>
              <Typography variant="body2">
                Координаты получены и отправлены
                <br />
                Точность: <strong>{accuracy.toFixed(1)}м</strong>
                <br />
                Измерений: {measurementCount}
              </Typography>
            </Alert>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 3 }}>
              <CheckCircle color="success" />
              <Typography variant="body1" color="success.main">
                Можно закрыть эту страницу
              </Typography>
            </Box>
          </Box>
        )}

        {status === 'error' && (
          <Box>
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>Ошибка</AlertTitle>
              <Typography variant="body2">
                {errorMessage}
              </Typography>
            </Alert>

            {token && (
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleRequestLocation}
                sx={{ mt: 2 }}
              >
                Попробовать снова
              </Button>
            )}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default MobileGeoPage;
